import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import history from '~/browserHistory';
import { Box, Center, Column, Pressable, Row } from '~/commonComponents';
import { getShouldShowBackgroundCheckBadge } from '~/growthbook';
import ChevronRight from '~/images/arrow_right.svg';
import defaultProfile from '~/images/HY_tooth.png';
import HeartBorderedIcon from '~/images/icons/HeartBorderedIcon';
import shieldCheckIcon from '~/images/shield-check.svg';
import RatingIcon from '~/images/star_icon.svg';
import { BackgroundCheckStatusEnum, IJob, IUser } from '~/models';
import { CompanyMatchLabel } from '../CandidateOverview/components';

interface IProps {
  hasWorkedWithDSO: boolean;
  hasWorkedWithOffice: boolean;
  jobId: string;
  job: IJob;
  candidate?: IUser;
  showLicense?: boolean;
  backgroundCheckStatus?: BackgroundCheckStatusEnum;
}

export const CandidateProfile = ({
  candidate,
  hasWorkedWithDSO,
  hasWorkedWithOffice,
  job,
  jobId,
  showLicense = false,
  backgroundCheckStatus,
}: IProps) => (
  <Box>
    <Row gap={20}>
      <Center
        border={`3px solid ${candidate?.isFavorite ? colors.favorites[400] : 'transparent'}`}
        borderRadius={94}
        height={94}
        padding={2}
        position="relative"
        width={94}
      >
        {candidate && candidate.profile_image ? (
          <img
            alt="profile_image"
            src={candidate.profile_image}
            style={{
              width: 94,
              height: 94,
              borderRadius: 94,
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box className="default_circle_container">
            <img src={defaultProfile} alt="profile_image" width="94px" />
          </Box>
        )}
        {candidate?.isFavorite && (
          <Box style={{ position: 'absolute', top: '72%', right: -4 }}>
            <HeartBorderedIcon />
          </Box>
        )}
      </Center>
      <Column justifyContent="center">
        <Row
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Typography
            as="span"
            bold
            color={colors.neutral[600]}
            size="heading_4"
            numberOfLines={1}
            style={{ margin: '0px 7px 0px 0px', maxWidth: 280 }}
          >
            {`${candidate?.first_name} ${candidate?.last_name}`}
          </Typography>

          <Row alignItems="center" gap={5}>
            <img
              src={RatingIcon}
              alt="green"
              style={{
                width: 20,
                height: 20,
                margin: '0px 5px 3px 0px',
              }}
            />
            <Typography as="span" bold size="body_large" color={colors.neutral[600]}>
              {candidate?.average_rating?.toFixed(1) || '5.0'}
            </Typography>
          </Row>

          <Box
            alignSelf="center"
            border={`1px solid ${colors.neutral[600]}`}
            height={16}
            marginHorizontal={8}
          />

          <Pressable
            onPress={() => {
              history.push(`/dashboard/candidate/${jobId}`, {
                candidateId: candidate?.id,
                state: job?.state,
                profession: job?.profession,
              });
            }}
          >
            <Row alignItems="center" cursor="pointer" gap={8}>
              <Typography as="span" bold color={colors.secondary[500]} size="heading_4">
                Profile
              </Typography>
              <img src={ChevronRight} alt="arrow" style={{ width: 16, height: 16 }} />
            </Row>
          </Pressable>
        </Row>

        {showLicense && (
          <>
            <Typography as="span" color={colors.neutral[600]} size="body_large">
              Drivers License Verified
            </Typography>

            <Row alignItems="center">
              <Typography as="span">
                {candidate?.license?.subProfession ?? candidate?.license?.profession} Credentials
                Verified
              </Typography>
              {backgroundCheckStatus === BackgroundCheckStatusEnum.Clear &&
                getShouldShowBackgroundCheckBadge() && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        alignSelf: 'center',
                        height: 16,
                        marginLeft: 8,
                        marginRight: 8,
                        border: `1px solid ${colors.neutral[500]}`,
                      }}
                    />
                    <Row alignItems="center" gap={4}>
                      <Typography as="span">Background Checked</Typography>
                      <img
                        src={shieldCheckIcon}
                        alt="shield-check"
                        style={{
                          width: 24,
                          height: 24,
                          margin: '0px 0px 0px 0px',
                        }}
                      />
                    </Row>
                  </>
                )}
            </Row>
          </>
        )}

        <CompanyMatchLabel
          offerOwnerId={job.offer_owner.id}
          professional={candidate}
          hasWorkedWithDSO={hasWorkedWithDSO}
          hasWorkedWithOffice={hasWorkedWithOffice}
        />
      </Column>
    </Row>
  </Box>
);
